/**
 * marangoniInk — ink-in-water driven by Marangoni surface-tension flow.
 *
 * Marangoni flow: a region of low surface tension (the ink) pulls the
 * surrounding water outward. The result is the characteristic
 * "fingers" of dye spreading in water — not diffusion, but active
 * mechanical pulling of the fluid by surface tension gradients.
 *
 * One mechanism: trace a Marangoni velocity field through 3D space
 * for each ink source, and deposit ink concentration along the path.
 * The shading is just concentration × thin-film interference phase —
 * no decorative layers, no "source cores", no "spore clouds", no
 * "front halos". The 3D depth and the physics ARE the visual.
 *
 * Reference: Probstein "Physicochemical Hydrodynamics".
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MarangoniInkPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}
const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
float bass; float mid; float high;

const float TAU = 6.283185307179586;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// Marangoni velocity field at point p. The velocity is:
//   - radial outward from each ink source (the surface tension pull)
//   - diffusion-like turbulence (curl noise)
// Both are tuned so the depth is visible — the field has structure,
// it isn't just a smooth gradient.
vec2 marangoniVel(vec2 p, float t) {
  // Two "sources" of influence: the 3 clock-rotating ink sources
  // + a slow-moving central attractor. The attractor is just a
  // slowly-orbiting point that creates the swirling pattern.
  vec2 v = vec2(0.0);
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float ph = t * 0.07 + fi * 2.1;
    vec2 src = vec2(cos(ph), sin(ph)) * 0.4;
    vec2 r = p - src;
    vec2 radial = r / max(length(r), 0.05);
    v += radial * 0.18;
  }
  // Curl-noise turbulence for organic advection.
  float e = 0.01;
  float a = vnoise(p + vec2(0.0, e)) - vnoise(p - vec2(0.0, e));
  float b = vnoise(p + vec2(e, 0.0)) - vnoise(p - vec2(e, 0.0));
  v += vec2(-a, b) / (2.0 * e);
  return v;
}

// Concentration at world point p, time t.
// Walk BACKWARD from p along the Marangoni velocity field — wherever
// we end up is "where the ink originally was". If that origin is
// inside one of the ink sources, p is inked.
float concentrationAt(vec2 p, float t) {
  float c = 0.0;
  // Multiple backward-walk distances (different "ages" of ink).
  for (int j = 0; j < 4; j++) {
    float fj = float(j);
    float age = 0.6 + 0.4 * fj;  // 0.6, 1.0, 1.4, 1.8 seconds
    vec2 q = p;
    for (int s = 0; s < 12; s++) {
      q -= marangoniVel(q, t - float(s) * 0.05) * 0.05;
    }
    // Did we end up near an ink source?
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      float ph = (t - age) * 0.07 + fi * 2.1;
      vec2 src = vec2(cos(ph), sin(ph)) * 0.4;
      float d = length(q - src);
      c += exp(-d * d * 6.0) * (1.0 - fj * 0.18);
    }
  }
  return c;
}

void main() {
  vec2 uv = (vUV - 0.5) * 2.0;
  uv.x *= uResolution.x / uResolution.y;

  float T = uTime;

  // Audio subtly perturbs the velocity field — just a 5% warp.
  // (No "bass pulse", no "mid amplitude", no explicit banding.)
  float audio = (uBass.x + uBass.y + uMid.y + uHigh.z) * 0.25;
  audio = pow(clamp(audio * 1.8, 0.0, 1.0), 0.7);

  // Concentration.
  float c = concentrationAt(uv, T);

  // Thin-film interference color from concentration + a slow
  // position-based phase. This is the ONLY color treatment.
  float phase = c * 1.8 + 0.04 * dot(uv, vec2(13.7, 7.3));
  vec3 col = 0.5 + 0.5 * cos(TAU * (phase + vec3(0.0, 0.33, 0.67)));

  // Dark water in low-concentration regions.
  col = mix(vec3(0.02, 0.04, 0.08), col, smoothstep(0.05, 0.5, c));

  // Slight saturation pulse from audio — fades the colors nicely.
  col *= 0.8 + 0.2 * audio;

  // Vignette.
  float vig = 1.0 - dot(uv, uv) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) o[i] = 0.5 + 0.3 * Math.sin(t * (0.8 + i * 0.17));
}
export function computeMarangoniInkBands(t: number, o: Float32Array): void { bands(t, o); }
export function computeMarangoniInkRealtimeBands(f: Uint8Array, o: Float32Array): void {
  const N = f.length;
  for (let i = 0; i < 9; i++) {
    const start = Math.floor(i / 9 * N);
    const end = Math.floor((i + 1) / 9 * N);
    let s = 0, n = 0;
    for (let k = start; k < end; k++) { s += f[k] || 0; n++; }
    o[i] = n > 0 ? (s / n) / 255 : 0;
  }
}
export function createMarangoniInkPipeline(gl: WebGL2RenderingContext): MarangoniInkPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}
export function drawMarangoniInk(gl: WebGL2RenderingContext, p: MarangoniInkPipeline, w: number, h: number, t: number): void {
  gl.useProgram(p.program);
  const prog = p.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), w, h);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), p.bands[0], p.bands[1], p.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), p.bands[3], p.bands[4], p.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), p.bands[6], p.bands[7], p.bands[8]);
  gl.bindVertexArray(p.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
export { VERT_SRC, FRAG_SRC };
